/**
 * The filter controls the Trajectories page and the Requests view share
 * (TRA-1397): the Filters popover with its metadata dropdowns, and the
 * row of removable chips that reads out what is active.
 *
 * The long tail of filters sits behind one button, so the toolbar keeps
 * only search, range and the one-click filters and the page reads as a
 * table with a toolbar rather than a form. Tags are the Trajectories
 * page's own axis: pass `tags` to show them, leave them out elsewhere.
 */

import { SlidersHorizontal, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { ANY, activeFilterCount, type MetadataFilter, type MetadataParam } from './listingParams'

/** The tag axis, Trajectories only: the vocabulary, the AND-matched
    selection and the toggle. */
export interface TagFilters {
  tags: string[]
  selectedTags: string[]
  onToggleTag: (tag: string) => void
}

const NO_TAGS: TagFilters = { tags: [], selectedTags: [], onToggleTag: () => undefined }

interface FilterHandlers<P extends MetadataParam> {
  filters: MetadataFilter<P>[]
  onMetadata: (param: P, value: string) => void
  tagFilters?: TagFilters
  /** The sentence for a popover with nothing to filter by. */
  emptyMessage: string
}

export function FilterPopover<P extends MetadataParam>({
  filters,
  onMetadata,
  tagFilters = NO_TAGS,
  emptyMessage,
}: FilterHandlers<P>): React.JSX.Element {
  const { tags, selectedTags, onToggleTag } = tagFilters
  // A dropdown with nothing to choose from is noise. Keep it when the URL
  // carries a value so a filter matching zero rows can still be cleared.
  const visible = filters.filter((f) => f.options.length > 0 || f.selected !== ANY)
  const activeCount = activeFilterCount(filters, selectedTags)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 px-3" aria-label="Filters">
          <SlidersHorizontal className="!size-3.5" />
          Filters
          {activeCount > 0 && <span className="tabular-nums">({activeCount})</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[420px] space-y-4 p-4">
        {visible.length === 0 && tags.length === 0 ? (
          <p className="text-xs text-muted-foreground">{emptyMessage}</p>
        ) : null}
        {visible.length > 0 && (
          <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2">
            {visible.map((f) => (
              <MetadataSelect key={f.param} filter={f} onChange={onMetadata} />
            ))}
          </div>
        )}
        {tags.length > 0 && (
          <div
            role="group"
            aria-label="Tag filters (runs must carry every selected tag)"
            className="space-y-2"
          >
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Tags
            </span>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => {
                const active = selectedTags.includes(tag)
                return (
                  <button
                    key={tag}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      onToggleTag(tag)
                    }}
                    className={`h-6 px-2.5 font-mono text-[11px] tracking-wider motion-safe:transition-colors ${
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tag}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/** Label + dropdown pair laid out by the parent grid, so labels align in a
    column and the dropdowns share one width. */
export function MetadataSelect<P extends MetadataParam>({
  filter,
  onChange,
}: {
  filter: MetadataFilter<P>
  onChange: (param: P, value: string) => void
}): React.JSX.Element {
  return (
    <>
      <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        {filter.label}
      </span>
      <Select
        value={filter.selected}
        onValueChange={(value) => {
          onChange(filter.param, value)
        }}
      >
        <SelectTrigger aria-label={`${filter.label} filter`} className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any</SelectItem>
          {filter.options.map((option) => (
            <SelectItem key={option.value} value={option.value} className="font-mono text-xs">
              {option.label}
            </SelectItem>
          ))}
          {filter.selected !== ANY && !filter.options.some((o) => o.value === filter.selected) && (
            <SelectItem value={filter.selected} className="font-mono text-xs">
              {filter.selected}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </>
  )
}

/** Readout of what the popover currently filters by, one removable chip
    each. Render it only when something is active, so an unfiltered page
    has no second row at all. */
export function ActiveFilters<P extends MetadataParam>({
  filters,
  onMetadata,
  tagFilters = NO_TAGS,
  onClear,
}: Omit<FilterHandlers<P>, 'emptyMessage'> & { onClear: () => void }): React.JSX.Element {
  const chips = [
    ...filters
      .filter((f) => f.selected !== ANY)
      .map((f) => ({
        key: f.param,
        text: `${f.label}: ${f.selected}`,
        remove: () => {
          onMetadata(f.param, ANY)
        },
      })),
    ...tagFilters.selectedTags.map((tag) => ({
      key: `tag:${tag}`,
      text: `Tag: ${tag}`,
      remove: () => {
        tagFilters.onToggleTag(tag)
      },
    })),
  ]
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Active filters">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex h-6 items-center gap-1 border border-border pl-2.5 pr-1 font-mono text-[11px] tracking-wider"
        >
          {chip.text}
          <button
            type="button"
            aria-label={`Remove ${chip.text}`}
            onClick={chip.remove}
            className="inline-flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClear}
        className="h-6 px-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      >
        Clear
      </button>
    </div>
  )
}
